/*
 * newsbot - Stories list with expandable rows
 */

import { useMemo, useState, useEffect, Fragment } from "react";
import { Link } from "@tanstack/react-router";
import { StoriesRoute } from "../router";
import { Menu, MenuButton, MenuItem, MenuItems, Transition } from "@headlessui/react";
import { ChevronDownIcon, ChevronRightIcon, XMarkIcon, ClipboardDocumentIcon, SparklesIcon, MagnifyingGlassIcon } from "@heroicons/react/24/outline";

import { EmptyListState } from "@components/emptystates";
import { toast } from "@components/hot-toast";
import Toast from "@components/notifications/Toast";
import { useStories, useCategories, useStoriesByCategory, useStoryArticles, useFeedGroupCounts } from "@api/queries";
import type { Story, Category, Article } from "@/types";
import { formatRelativeTime, classNames } from "@utils";
import { getFaviconUrl } from "@utils/favicon";
import { StoryExpandedRow } from "@components/stories/StoryExpandedRow";
import { ImportanceBadge } from "@components/stories/ImportanceBadge";
import { SourceFavicons } from "@components/stories/SourceFavicons";

function CategoryBadge({ category }: { category: Story["category"] }) {
  if (!category) {
    return <span className="text-gray-400 dark:text-gray-500 text-xs">-</span>;
  }

  return (
    <span className="inline-flex items-center rounded-md bg-purple-50 dark:bg-purple-900/30 px-2 py-0.5 text-xs font-medium text-purple-700 dark:text-purple-300">
      {category.name}
    </span>
  );
}

function ArticleRow({ article }: { article: Article }) {
  const domain = (() => {
    try {
      const url = new URL(article.url);
      return url.hostname.replace(/^www\./, '');
    } catch {
      return '';
    }
  })();

  return (
    <tr className="bg-gray-50 dark:bg-gray-850">
      <td colSpan={6} className="pl-12 pr-5 py-2">
        <div className="flex items-center gap-2 text-sm">
          <span className="text-gray-400 dark:text-gray-500">└─</span>
          {domain && (
            <img
              src={getFaviconUrl(domain)}
              alt=""
              className="w-4 h-4"
              onError={(e) => { e.currentTarget.style.display = 'none'; }}
            />
          )}
          <span className="text-gray-500 dark:text-gray-400 text-xs min-w-[100px]">
            {domain}
          </span>
          <span className="text-gray-300 dark:text-gray-600">·</span>
          <a
            href={article.url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 dark:text-blue-400 hover:underline truncate max-w-xl"
          >
            {article.title}
          </a>
          {article.published_at && (
            <>
              <span className="text-gray-300 dark:text-gray-600">·</span>
              <span className="text-gray-400 dark:text-gray-500 text-xs whitespace-nowrap">
                {formatRelativeTime(article.published_at)}
              </span>
            </>
          )}
          <a
            href={article.url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 ml-auto"
            title="Open in new tab"
          >
            ↗
          </a>
        </div>
      </td>
    </tr>
  );
}

function ExpandedArticles({ storyId }: { storyId: string }) {
  const { data: articles, isLoading } = useStoryArticles(storyId);

  if (isLoading) {
    return (
      <tr className="bg-gray-50 dark:bg-gray-850">
        <td colSpan={6} className="pl-12 pr-5 py-2 text-sm text-gray-500">
          Loading articles...
        </td>
      </tr>
    );
  }

  if (!articles || articles.length === 0) {
    return (
      <tr className="bg-gray-50 dark:bg-gray-850">
        <td colSpan={6} className="pl-12 pr-5 py-2 text-sm text-gray-500">
          No articles found
        </td>
      </tr>
    );
  }

  return (
    <>
      {articles.map((article) => (
        <ArticleRow key={article.id} article={article} />
      ))}
    </>
  );
}

function StoryRow({ story }: { story: Story }) {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <>
      <tr
        className="hover:bg-gray-50 dark:hover:bg-gray-750 cursor-pointer focus:outline-none focus:bg-gray-50 dark:focus:bg-gray-750"
        onClick={() => setIsExpanded(!isExpanded)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setIsExpanded(!isExpanded);
          }
        }}
        tabIndex={0}
        role="button"
        aria-expanded={isExpanded}
        aria-label={story.headline || "Untitled story"}
      >
        <td className="hidden sm:table-cell pl-5 pr-3 py-3 whitespace-nowrap">
          <div className="flex items-center gap-2">
            <ChevronRightIcon
              className={classNames(
                "w-4 h-4 text-gray-400 transition-transform",
                isExpanded && "rotate-90"
              )}
            />
            <span className="text-sm text-gray-600 dark:text-gray-400">
              {formatRelativeTime(story.last_article_at || story.first_seen_at)}
            </span>
          </div>
        </td>
        <td className="pl-4 sm:pl-3 px-3 py-3">
          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-2">
              <ChevronRightIcon
                className={classNames(
                  "sm:hidden w-4 h-4 shrink-0 text-gray-400 transition-transform",
                  isExpanded && "rotate-90"
                )}
              />
              <Link
                to="/stories/$storyId"
                params={{ storyId: story.id }}
                className="text-sm font-medium text-gray-900 dark:text-gray-100 hover:text-blue-600 dark:hover:text-blue-400 line-clamp-2 sm:line-clamp-1"
                title={story.headline || "Untitled"}
                onClick={(e) => e.stopPropagation()}
              >
                {story.headline || "Untitled"}
              </Link>
              {story.has_suggestions && (
                <span className="hidden sm:inline-flex items-center gap-1 px-1.5 py-0.5 text-xs font-medium text-purple-700 dark:text-purple-300 bg-purple-100 dark:bg-purple-900/30 rounded shrink-0">
                  <SparklesIcon className="w-3 h-3" />
                  Suggestions
                </span>
              )}
            </div>
            {/* Mobile: show metadata inline under headline */}
            <div className="flex items-center gap-2 sm:hidden text-xs text-gray-500 dark:text-gray-400">
              <span>{formatRelativeTime(story.last_article_at || story.first_seen_at)}</span>
              <span>·</span>
              <span>{story.article_count} articles</span>
              {story.category && (
                <>
                  <span>·</span>
                  <span className="text-purple-600 dark:text-purple-400">{story.category.name}</span>
                </>
              )}
              {story.importance_score != null && (
                <>
                  <span>·</span>
                  <span className="font-medium text-gray-700 dark:text-gray-300">{(story.importance_score * 10).toFixed(1)}</span>
                </>
              )}
            </div>
            {story.tags && story.tags.length > 0 && (
              <div className="hidden sm:flex gap-1 flex-wrap">
                {story.tags.slice(0, 3).map((tag) => (
                  <span
                    key={tag}
                    className="inline-flex items-center px-1.5 py-0.5 text-xs rounded bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400"
                  >
                    {tag}
                  </span>
                ))}
                {story.tags.length > 3 && (
                  <span className="text-xs text-gray-400">
                    +{story.tags.length - 3}
                  </span>
                )}
              </div>
            )}
          </div>
        </td>
        <td className="hidden lg:table-cell px-3 py-3 whitespace-nowrap">
          <SourceFavicons domains={story.source_domains} />
        </td>
        <td className="hidden lg:table-cell px-3 py-3 whitespace-nowrap">
          <CategoryBadge category={story.category} />
        </td>
        <td className="hidden sm:table-cell px-3 py-3 whitespace-nowrap">
          <span className="text-sm text-gray-600 dark:text-gray-400">
            {story.article_count}
          </span>
        </td>
        <td className="hidden sm:table-cell px-3 pr-5 py-3 whitespace-nowrap">
          <ImportanceBadge score={story.importance_score} />
        </td>
      </tr>
      {isExpanded && (
        <>
          {/* Show extraction data if available */}
          {story.extractions && story.extractions.length > 0 && (
            <StoryExpandedRow story={story} />
          )}
          {/* Show articles */}
          <ExpandedArticles storyId={story.id} />
        </>
      )}
    </>
  );
}

interface CategoryFilterProps {
  categories: Category[];
  selectedCategory: string | null;
  onSelect: (slug: string | null) => void;
}

function CategoryFilter({ categories, selectedCategory, onSelect }: CategoryFilterProps) {
  const selectedCat = categories.find((c) => c.slug === selectedCategory);
  const displayName = selectedCategory === "__uncategorized__"
    ? "Uncategorized"
    : selectedCat?.name || "All Categories";

  return (
    <div className="flex items-center gap-2">
      <span className="text-sm text-gray-500 dark:text-gray-400">Category:</span>
      <Menu as="div" className="relative">
        <MenuButton className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500">
          {displayName}
          <ChevronDownIcon className="w-4 h-4" />
        </MenuButton>
        <Transition
          as={Fragment}
          enter="transition ease-out duration-100"
          enterFrom="transform opacity-0 scale-95"
          enterTo="transform opacity-100 scale-100"
          leave="transition ease-in duration-75"
          leaveFrom="transform opacity-100 scale-100"
          leaveTo="transform opacity-0 scale-95"
        >
          <MenuItems className="absolute left-0 mt-2 w-56 bg-white dark:bg-gray-800 rounded-md shadow-lg border border-gray-200 dark:border-gray-700 focus:outline-none z-10">
            <div className="py-1">
              <MenuItem>
                {({ active }) => (
                  <button
                    className={classNames(
                      active ? "bg-gray-100 dark:bg-gray-700" : "",
                      "w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300"
                    )}
                    onClick={() => onSelect(null)}
                  >
                    All Categories
                  </button>
                )}
              </MenuItem>
              <MenuItem>
                {({ active }) => (
                  <button
                    className={classNames(
                      active ? "bg-gray-100 dark:bg-gray-700" : "",
                      selectedCategory === "__uncategorized__" ? "bg-yellow-50 dark:bg-yellow-900/30" : "",
                      "w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300"
                    )}
                    onClick={() => onSelect("__uncategorized__")}
                  >
                    Uncategorized
                  </button>
                )}
              </MenuItem>
              <div className="border-t border-gray-200 dark:border-gray-700 my-1" />
              {categories
                .filter((c) => c.enabled)
                .map((cat) => (
                  <MenuItem key={cat.id}>
                    {({ active }) => (
                      <button
                        className={classNames(
                          active ? "bg-gray-100 dark:bg-gray-700" : "",
                          selectedCategory === cat.slug ? "bg-blue-50 dark:bg-blue-900/30" : "",
                          "w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300"
                        )}
                        onClick={() => onSelect(cat.slug)}
                      >
                        {cat.name}
                      </button>
                    )}
                  </MenuItem>
                ))}
            </div>
          </MenuItems>
        </Transition>
      </Menu>
      {selectedCategory && (
        <button
          type="button"
          onClick={() => onSelect(null)}
          className="inline-flex items-center justify-center p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          title="Clear filter"
        >
          <XMarkIcon className="w-4 h-4" />
        </button>
      )}
    </div>
  );
}

interface TagsFilterProps {
  availableTags: string[];
  selectedTags: string[];
  onToggle: (tag: string) => void;
  onClear: () => void;
}

function TagsFilter({ availableTags, selectedTags, onToggle, onClear }: TagsFilterProps) {
  if (availableTags.length === 0) return null;

  return (
    <div className="flex items-center gap-2 flex-wrap">
      <span className="text-sm text-gray-500 dark:text-gray-400">Tags:</span>
      {availableTags.map((tag) => (
        <button
          key={tag}
          type="button"
          onClick={() => onToggle(tag)}
          className={classNames(
            "px-2 py-2 text-xs font-medium rounded-md transition-colors",
            selectedTags.includes(tag)
              ? "bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-200"
              : "bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600"
          )}
        >
          {tag}
        </button>
      ))}
      {selectedTags.length > 0 && (
        <button
          type="button"
          onClick={onClear}
          className="text-xs text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
        >
          Clear
        </button>
      )}
    </div>
  );
}

const HOURS_OPTIONS = [
  { value: "1", label: "1 hour" },
  { value: "6", label: "6 hours" },
  { value: "12", label: "12 hours" },
  { value: "24", label: "24 hours" },
  { value: "48", label: "48 hours" },
  { value: "72", label: "72 hours" },
  { value: "168", label: "1 week" },
];

const LIMIT_OPTIONS = [
  { value: "5", label: "5" },
  { value: "10", label: "10" },
  { value: "20", label: "20" },
  { value: "50", label: "50" },
];

function ExportApiSection({ vertical }: { vertical: string | null }) {
  const [hours, setHours] = useState("24");
  const [limit, setLimit] = useState("10");

  const exportUrl = `${window.location.origin}/api/v1/export/top-stories?since_hours=${hours}&limit=${limit}${vertical ? `&feed_group=${vertical}` : ""}`;

  const copyToClipboard = async () => {
    try {
      // Try modern clipboard API first (requires HTTPS or localhost)
      await navigator.clipboard.writeText(exportUrl);
      toast.custom((t) => (
        <Toast type="success" body="Export URL copied to clipboard" t={t} />
      ));
    } catch {
      // Fallback for HTTP: use textarea + execCommand
      const textarea = document.createElement("textarea");
      textarea.value = exportUrl;
      textarea.style.position = "fixed";
      textarea.style.opacity = "0";
      document.body.appendChild(textarea);
      textarea.select();
      try {
        document.execCommand("copy");
        toast.custom((t) => (
          <Toast type="success" body="Export URL copied to clipboard" t={t} />
        ));
      } catch {
        toast.custom((t) => (
          <Toast type="error" body="Failed to copy - please copy manually" t={t} />
        ));
        console.error("Copy failed. URL:", exportUrl);
      }
      document.body.removeChild(textarea);
    }
  };

  const hoursLabel = HOURS_OPTIONS.find((o) => o.value === hours)?.label || hours;
  const limitLabel = LIMIT_OPTIONS.find((o) => o.value === limit)?.label || limit;

  return (
    <div className="flex items-center gap-2 flex-wrap">
      <span className="text-sm text-gray-500 dark:text-gray-400">Export API:</span>

      {/* Hours dropdown */}
      <Menu as="div" className="relative">
        <MenuButton className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700">
          {hoursLabel}
          <ChevronDownIcon className="w-3 h-3" />
        </MenuButton>
        <Transition
          as={Fragment}
          enter="transition ease-out duration-100"
          enterFrom="transform opacity-0 scale-95"
          enterTo="transform opacity-100 scale-100"
          leave="transition ease-in duration-75"
          leaveFrom="transform opacity-100 scale-100"
          leaveTo="transform opacity-0 scale-95"
        >
          <MenuItems className="absolute left-0 mt-1 w-28 bg-white dark:bg-gray-800 rounded-md shadow-lg border border-gray-200 dark:border-gray-700 focus:outline-none z-10">
            <div className="py-1">
              {HOURS_OPTIONS.map((opt) => (
                <MenuItem key={opt.value}>
                  {({ active }) => (
                    <button
                      className={classNames(
                        active ? "bg-gray-100 dark:bg-gray-700" : "",
                        hours === opt.value ? "bg-blue-50 dark:bg-blue-900/30" : "",
                        "w-full text-left px-3 py-1.5 text-xs text-gray-700 dark:text-gray-300"
                      )}
                      onClick={() => setHours(opt.value)}
                    >
                      {opt.label}
                    </button>
                  )}
                </MenuItem>
              ))}
            </div>
          </MenuItems>
        </Transition>
      </Menu>

      {/* Limit dropdown */}
      <Menu as="div" className="relative">
        <MenuButton className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700">
          Limit: {limitLabel}
          <ChevronDownIcon className="w-3 h-3" />
        </MenuButton>
        <Transition
          as={Fragment}
          enter="transition ease-out duration-100"
          enterFrom="transform opacity-0 scale-95"
          enterTo="transform opacity-100 scale-100"
          leave="transition ease-in duration-75"
          leaveFrom="transform opacity-100 scale-100"
          leaveTo="transform opacity-0 scale-95"
        >
          <MenuItems className="absolute left-0 mt-1 w-20 bg-white dark:bg-gray-800 rounded-md shadow-lg border border-gray-200 dark:border-gray-700 focus:outline-none z-10">
            <div className="py-1">
              {LIMIT_OPTIONS.map((opt) => (
                <MenuItem key={opt.value}>
                  {({ active }) => (
                    <button
                      className={classNames(
                        active ? "bg-gray-100 dark:bg-gray-700" : "",
                        limit === opt.value ? "bg-blue-50 dark:bg-blue-900/30" : "",
                        "w-full text-left px-3 py-1.5 text-xs text-gray-700 dark:text-gray-300"
                      )}
                      onClick={() => setLimit(opt.value)}
                    >
                      {opt.label}
                    </button>
                  )}
                </MenuItem>
              ))}
            </div>
          </MenuItems>
        </Transition>
      </Menu>

      {/* Copy button */}
      <button
        type="button"
        onClick={copyToClipboard}
        className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 rounded-md hover:bg-blue-100 dark:hover:bg-blue-900/50"
        title={exportUrl}
      >
        <ClipboardDocumentIcon className="w-3.5 h-3.5" />
        Copy URL
      </button>
    </div>
  );
}

export const Stories = () => {
  const search = StoriesRoute.useSearch();
  const navigate = StoriesRoute.useNavigate();

  const selectedCategory = search.category || null;
  const selectedTags = search.tags ? search.tags.split(",") : [];
  const vertical = search.vertical || null;
  const page = search.page;
  const sortBy = search.sort;
  const sortDir = search.dir;
  const minImportance = search.minImportance;
  const limit = 50;
  const offset = page * limit;

  // Local state only for the raw (un-debounced) search input
  const [searchQuery, setSearchQuery] = useState(search.q || "");
  const [debouncedSearch, setDebouncedSearch] = useState(search.q || "");

  // Helpers to update URL search params
  const setPage = (p: number) =>
    navigate({ search: (prev) => ({ ...prev, page: p }) });
  const setSortBy = (s: string) =>
    navigate({ search: (prev) => ({ ...prev, sort: s, page: 0 }) });
  const setSortDir = (d: string) =>
    navigate({ search: (prev) => ({ ...prev, dir: d, page: 0 }) });
  const setMinImportance = (v: number | undefined) =>
    navigate({ search: (prev) => ({ ...prev, minImportance: v, page: 0 }) });
  const setSelectedCategory = (slug: string | null) =>
    navigate({ search: (prev) => ({ ...prev, category: slug || "", tags: "", page: 0 }) });
  const setVertical = (slug: string | null) =>
    navigate({ search: (prev) => ({ ...prev, vertical: slug || "", category: "", tags: "", page: 0 }) });
  const setSelectedTagsFn = (tags: string[]) =>
    navigate({ search: (prev) => ({ ...prev, tags: tags.join(","), page: 0 }) });

  // Debounce search input → update URL q param
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
      navigate({ search: (prev) => ({ ...prev, q: searchQuery, page: 0 }) });
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]); // eslint-disable-line react-hooks/exhaustive-deps

  const { data: feedGroupCountsData } = useFeedGroupCounts();
  const activeFeedGroupId = vertical
    ? feedGroupCountsData?.groups.find((g) => g.slug === vertical)?.id
    : undefined;
  const { data: categories } = useCategories(vertical ? { feed_group_id: activeFeedGroupId } : undefined);

  // Use filtered query when category/tags selected, otherwise show all stories
  const { isLoading: isLoadingAll, isFetching: isFetchingAll, data: allStoriesData } = useStories({
    limit,
    offset,
    sort_by: sortBy,
    sort_dir: sortDir,
    min_importance: minImportance,
    search: debouncedSearch || undefined,
    feed_group: vertical || undefined,
  });
  const { isLoading: isLoadingFiltered, isFetching: isFetchingFiltered, data: filteredStoriesData } = useStoriesByCategory({
    category: selectedCategory || undefined,
    tags: selectedTags.length > 0 ? selectedTags : undefined,
    limit,
    offset,
    sort_by: sortBy,
    sort_dir: sortDir,
    min_importance: minImportance,
    search: debouncedSearch || undefined,
    feed_group: vertical || undefined,
  });

  const hasFilters = selectedCategory || selectedTags.length > 0;
  const isLoading = hasFilters ? isLoadingFiltered : isLoadingAll;
  const isFetching = hasFilters ? isFetchingFiltered : isFetchingAll;
  const data = hasFilters ? filteredStoriesData : allStoriesData;
  const stories = data?.items;

  const availableTags = useMemo(() => {
    if (!stories) return [];
    const tagSet = new Set<string>();
    stories.forEach((story) => {
      story.tags?.forEach((tag) => tagSet.add(tag));
    });
    return Array.from(tagSet).sort();
  }, [stories]);

  const handleCategoryChange = (slug: string | null) => {
    setSelectedCategory(slug);
  };

  const handleVerticalChange = (slug: string | null) => {
    setVertical(slug);
  };

  const handleTagToggle = (tag: string) => {
    const next = selectedTags.includes(tag)
      ? selectedTags.filter((t) => t !== tag)
      : [...selectedTags, tag];
    setSelectedTagsFn(next);
  };

  const hasAnyFilters = selectedCategory || selectedTags.length > 0 || debouncedSearch || minImportance !== undefined || sortBy !== "last_article_at" || sortDir !== "desc";

  const clearAllFilters = () => {
    setSearchQuery("");
    setDebouncedSearch("");
    navigate({
      search: {
        page: 0,
        sort: "last_article_at",
        dir: "desc",
        q: "",
        minImportance: undefined,
        category: "",
        tags: "",
        vertical: "",
      },
    });
  };

  return (
    <main>
      <div className="my-6 max-w-(--breakpoint-xl) mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="text-3xl font-bold text-black dark:text-white">Stories</h1>
      </div>

      <div className="max-w-(--breakpoint-xl) mx-auto pb-6 px-2 sm:px-6 lg:pb-16 lg:px-8">
        {/* Vertical Tabs */}
        {feedGroupCountsData && (
          <div className="relative flex items-center gap-1 border-b border-gray-200 dark:border-gray-700 mb-4 overflow-x-auto">
            <button
              onClick={() => handleVerticalChange(null)}
              className={classNames(
                "px-3 py-2 text-sm font-medium border-b-2 -mb-px transition-colors whitespace-nowrap",
                !vertical
                  ? "border-blue-500 text-blue-600 dark:text-blue-400"
                  : "border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              )}
            >
              All
              <span className="ml-1.5 text-xs text-gray-400">({feedGroupCountsData.total ?? 0})</span>
            </button>
            {feedGroupCountsData.groups.map((group) => (
              <button
                key={group.slug}
                onClick={() => handleVerticalChange(group.slug)}
                className={classNames(
                  "px-3 py-2 text-sm font-medium border-b-2 -mb-px transition-colors whitespace-nowrap",
                  vertical === group.slug
                    ? "border-blue-500 text-blue-600 dark:text-blue-400"
                    : "border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                )}
              >
                {group.name}
                <span className="ml-1.5 text-xs text-gray-400">({group.story_count})</span>
              </button>
            ))}
          </div>
        )}

        {/* Filters */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
          <div className="flex flex-col sm:flex-row sm:items-center gap-4">
            {vertical && categories && (
              <CategoryFilter
                categories={categories}
                selectedCategory={selectedCategory}
                onSelect={handleCategoryChange}
              />
            )}
            {selectedCategory && availableTags.length > 0 && (
              <TagsFilter
                availableTags={availableTags}
                selectedTags={selectedTags}
                onToggle={handleTagToggle}
                onClear={() => setSelectedTagsFn([])}
              />
            )}
          </div>
          <div className="hidden sm:block">
            <ExportApiSection vertical={vertical} />
          </div>
        </div>

        {/* Sort, Search, and Importance Filter Controls */}
        <div className="flex flex-wrap items-center gap-3 mb-4">
          {/* Search */}
          <div className="relative flex-1 min-w-[200px]">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search stories..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-9 pr-3 py-2 text-sm rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          {/* Sort By */}
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="px-3 py-2 text-sm rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
          >
            <option value="last_article_at">Newest</option>
            <option value="importance_score">Importance</option>
            <option value="article_count">Article Count</option>
          </select>
          {/* Sort Direction */}
          <button
            onClick={() => setSortDir(sortDir === "desc" ? "asc" : "desc")}
            className="p-2 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700"
            title={sortDir === "desc" ? "Descending" : "Ascending"}
          >
            {sortDir === "desc" ? "↓" : "↑"}
          </button>
          {/* Importance Filter */}
          <select
            value={minImportance ?? ""}
            onChange={(e) => setMinImportance(e.target.value ? Number(e.target.value) : undefined)}
            className="px-3 py-2 text-sm rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
          >
            <option value="">Any importance</option>
            <option value="0.3">0.3+</option>
            <option value="0.5">0.5+</option>
            <option value="0.7">0.7+</option>
          </select>
          {/* Clear all filters button */}
          {hasAnyFilters && (
            <button
              type="button"
              onClick={clearAllFilters}
              className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
            >
              Clear all
            </button>
          )}
        </div>

        {isLoading ? (
          <div className="animate-pulse">
            <EmptyListState text="Loading stories..." />
          </div>
        ) : !stories || stories.length === 0 ? (
          <div className="bg-white dark:bg-gray-800 border border-gray-250 dark:border-gray-775 shadow-table rounded-md">
            <div className="flex items-center justify-center py-16">
              <EmptyListState text="No stories found" />
            </div>
          </div>
        ) : (
          <>
            <div className={classNames("w-full mt-4 mb-2", isFetching && !isLoading && "opacity-60 transition-opacity")}>
              <div className="bg-white dark:bg-gray-800 border border-gray-250 dark:border-gray-775 shadow-table rounded-md overflow-auto">
                <table aria-label="Top Stories" className="w-full rounded-md divide-y divide-gray-200 dark:divide-gray-750">
                  <thead className="bg-gray-100 dark:bg-gray-850">
                    <tr>
                      <th scope="col" className="hidden sm:table-cell w-24 pl-5 pr-3 py-3 text-xs font-medium tracking-wider text-left uppercase text-gray-600 dark:text-gray-400 rounded-tl-md">
                        Updated
                      </th>
                      <th scope="col" className="pl-4 sm:pl-3 px-3 py-3 text-xs font-medium tracking-wider text-left uppercase text-gray-600 dark:text-gray-400 sm:rounded-none rounded-tl-md">
                        Headline
                      </th>
                      <th scope="col" className="hidden lg:table-cell w-32 px-3 py-3 text-xs font-medium tracking-wider text-left uppercase text-gray-600 dark:text-gray-400">
                        Sources
                      </th>
                      <th scope="col" className="hidden lg:table-cell w-28 px-3 py-3 text-xs font-medium tracking-wider text-left uppercase text-gray-600 dark:text-gray-400">
                        Category
                      </th>
                      <th scope="col" className="hidden sm:table-cell w-20 px-3 py-3 text-xs font-medium tracking-wider text-left uppercase text-gray-600 dark:text-gray-400">
                        Articles
                      </th>
                      <th scope="col" className="hidden sm:table-cell w-24 px-3 pr-5 py-3 text-xs font-medium tracking-wider text-left uppercase text-gray-600 dark:text-gray-400 rounded-tr-md">
                        Importance
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-150 dark:divide-gray-750">
                    {stories.map((story) => (
                      <StoryRow key={story.id} story={story} />
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {data && (
              <div className="flex items-center justify-between mt-4 px-2">
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Showing {offset + 1}–{Math.min(offset + limit, data.total)} of {data.total}
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={() => setPage(Math.max(0, page - 1))}
                    disabled={page === 0}
                    className="px-3 py-1.5 text-sm rounded-md bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 disabled:opacity-50 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setPage(page + 1)}
                    disabled={offset + limit >= data.total}
                    className="px-3 py-1.5 text-sm rounded-md bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 disabled:opacity-50 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </main>
  );
};
